import { type FunctionComponent, useMemo } from "react";
import classnames from "classnames";

import { OddsSize } from "@ppb/the-wall-common/types";
import type { BetSelectionDetailsWebProps } from "./BetSelectionDetails.types";
import { SystemIconName, ValueIconName } from "@ppb/the-wall-icons";
import { GenericIcon } from "@ppb/the-wall-icons/GenericIcon/GenericIcon";

import { OddsMovement, StatusLabel, Label } from "@ppb/the-wall-web";
import { Odds } from "@ppb/the-wall-web/components/bricks/Indicators/Odds/Odds";

import styles from "./BetSelectionDetails.web.css";

export const BetSelectionDetails: FunctionComponent<BetSelectionDetailsWebProps> = ({
  isPlacing,
  sideLabel,
  sideTheme,
  icon,
  sportIcon,
  title,
  is90Min,
  selectionTypeIcon,
  racingLabel,
  oddsMovement,
  previousOdd,
  odd,
  onSelectionRemove,
  removeButtonLabel,
  subtitle,
  statusLabel,
}) => {
  const selectionTypeIconComponent = useMemo(() => {
    const iconName = (is90Min && ValueIconName.NINETY_MINUTE_PAYOUT_MONOCHROME) || selectionTypeIcon;

    return (
      iconName && (
        <span
          className={classnames(styles.selectionTypeIcon, {
            [styles.selectionTypeIconDisabled]: isPlacing,
          })}
        >
          <GenericIcon name={iconName} />
        </span>
      )
    );
  }, [is90Min, isPlacing, selectionTypeIcon]);

  return (
    <article className={styles.betSelectionDetails}>
      <section className={styles.selectionContainer}>
        <div className={styles.mainSection}>
          <div className={styles.titleGroup}>
            {sideLabel && sideTheme && (
              <div className={styles.sideContainer}>
                <Label text={sideLabel} theme={sideTheme} />
              </div>
            )}

            {icon ? <div className={styles.silkContainer}>{icon}</div> : sportIcon}

            <div className={styles.titleContainer}>
              <span className={styles.title}>{title}</span>
            </div>
          </div>
        </div>

        <div className={styles.secondaryGroup}>
          {(!!subtitle || !!statusLabel) && (
            <div className={styles.secondarySection}>
              <span className={styles.subtitle}>{subtitle}</span>
              {!!statusLabel && <StatusLabel {...statusLabel} />}
            </div>
          )}
        </div>
      </section>
      <div className={styles.oddsContainer}>
        {!!racingLabel && (
          <div className={styles.racingLabel}>
            <Label text={racingLabel} />
          </div>
        )}
        {selectionTypeIconComponent}
        {!!odd && (
          <div className={styles.odd}>
            <Odds
              icon={oddsMovement ? <OddsMovement direction={oddsMovement} disabled={false} /> : undefined}
              size={OddsSize.MEDIUM}
              value={odd}
              previousValue={previousOdd}
            />
          </div>
        )}

        {onSelectionRemove && (
          <button
            className={classnames(styles.remove, {
              [styles.removeDisabled]: isPlacing,
            })}
            onClick={onSelectionRemove}
            disabled={isPlacing}
            type="button"
            aria-label={removeButtonLabel}
          >
            <GenericIcon name={SystemIconName.CLOSE} />
          </button>
        )}
      </div>
    </article>
  );
};
